import React from 'react';
import { Search } from 'lucide-react';

import Dropdowns from 'components/Dropdowns';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Input } from 'components/ui/input';

import CustomerCard from '../../customer-data/components/CustomerCard';
import BranchCard from '../../customer-data/components/BranchCard';
import { CustomerData, BranchData } from '../../customer-data/api';

import { fetchKeyAccountProfitability, fetchKeyAccountProfitabilityBranches } from '../api';
import UnitToggle, { ConversionType } from '../components/UnitToggle';
import PaginationFooter from '../components/PaginationFooter';
import ProfitabilityChart from '../components/ProfitabilityChart';

interface Props {
    customerCode: string;
    conversionType: ConversionType;
    onConversionChange: (type: ConversionType) => void;
}

const RevenueProfitabilityTab: React.FC<Props> = ({
    customerCode,
    conversionType,
    onConversionChange,
}) => {
    const {
        years,
        year,
        levels,
        loading,
        error,
        handleSelectChange,
        handleBatchSelect,
        handleYearChange,
        getCurrentFilters,
    } = useCustomerDataFilters();

    // customer month-wise table
    const [customer, setCustomer] = React.useState<CustomerData | null>(null);
    const [customerLoading, setCustomerLoading] = React.useState<boolean>(false);
    const [dataError, setDataError] = React.useState<string>('');
    const lastCustomerQueryRef = React.useRef<string | null>(null);

    // branch drill-down
    const [showBranches, setShowBranches] = React.useState<boolean>(false);
    const [branches, setBranches] = React.useState<BranchData[]>([]);
    const [branchTotal, setBranchTotal] = React.useState<number>(0);
    const [branchLoading, setBranchLoading] = React.useState<boolean>(false);
    const [branchSearch, setBranchSearch] = React.useState<string>('');
    const [debouncedBranchSearch, setDebouncedBranchSearch] = React.useState<string>('');
    const [branchPage, setBranchPage] = React.useState<number>(1);
    const [branchPageSize, setBranchPageSize] = React.useState<number>(20);
    const lastBranchQueryRef = React.useRef<string | null>(null);

    const { isLoading: smoothCustomerLoading, showContent: showCustomerContent } =
        useSmoothLoading(customerLoading, { minLoadingTime: 200, debounceTime: 200 });
    const { isLoading: smoothBranchLoading, showContent: showBranchContent } =
        useSmoothLoading(branchLoading, { minLoadingTime: 200, debounceTime: 200 });

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedBranchSearch(branchSearch.trim());
            setBranchPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [branchSearch]);

    // fetch the customer's month-wise table
    React.useEffect(() => {
        if (!year || !customerCode || levels.length === 0) return;

        const filters = getCurrentFilters();
        const queryKey = JSON.stringify({ year, filters, customerCode });
        if (lastCustomerQueryRef.current === queryKey) return;
        lastCustomerQueryRef.current = queryKey;

        setCustomerLoading(true);
        setDataError('');

        fetchKeyAccountProfitability({ customer_code: customerCode, year, filters })
            .then((res) => setCustomer(res.customers[0] ?? null))
            .catch((err: unknown) => {
                const isForbidden = (err as any)?.status === 403;
                setDataError(
                    isForbidden
                        ? 'You are not allowed to access this resource.'
                        : err instanceof Error
                            ? err.message
                            : 'Failed to fetch profitability data'
                );
                setCustomer(null);
            })
            .finally(() => setCustomerLoading(false));
    }, [year, levels, customerCode, getCurrentFilters]);

    // fetch branch breakdown when drilled in
    React.useEffect(() => {
        if (!showBranches || !year || !customerCode || levels.length === 0) return;

        const filters = getCurrentFilters();
        const queryKey = JSON.stringify({
            year, filters, customerCode,
            search: debouncedBranchSearch, page: branchPage, pageSize: branchPageSize,
        });
        if (lastBranchQueryRef.current === queryKey) return;
        lastBranchQueryRef.current = queryKey;

        setBranchLoading(true);

        fetchKeyAccountProfitabilityBranches({
            customer_code: customerCode,
            year,
            filters,
            ...(debouncedBranchSearch && { search: debouncedBranchSearch }),
            sort_by: 'total_income',
            sort_order: 'desc',
            page: branchPage,
            page_size: branchPageSize,
        })
            .then((res) => {
                setBranches(res.branches);
                setBranchTotal(res.total);
            })
            .catch(() => {
                setBranches([]);
                setBranchTotal(0);
            })
            .finally(() => setBranchLoading(false));
    }, [showBranches, year, levels, customerCode, debouncedBranchSearch, branchPage, branchPageSize, getCurrentFilters]);

    return (
        <div className="space-y-5">
            {/* Filters */}
            <Dropdowns
                year={year}
                years={years}
                levels={levels}
                loading={loading}
                onSelectChange={handleSelectChange}
                onBatchSelect={handleBatchSelect}
                onYearChange={handleYearChange}
            />

            {(error || dataError) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error || dataError}</p>
                </div>
            )}

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                {showBranches ? (
                    <div className="flex items-center gap-4 flex-1">
                        <button
                            onClick={() => {
                                setShowBranches(false);
                                setBranchSearch('');
                            }}
                            className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300 whitespace-nowrap"
                        >
                            ← Back
                        </button>
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search for a branch..."
                                value={branchSearch}
                                onChange={(e) => setBranchSearch(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-600">
                        Click the table to view the branch/project-wise breakdown.
                    </p>
                )}

                <UnitToggle value={conversionType} onChange={onConversionChange} />
            </div>

            {/* Customer month-wise table */}
            {!showBranches && (
                <>
                    {smoothCustomerLoading && (
                        <div className="bg-white rounded-lg border shadow-sm">
                            <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
                            <div className="p-6 space-y-4">
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                            </div>
                        </div>
                    )}

                    {!smoothCustomerLoading && !customer && !dataError && (
                        <div className="text-center py-12 text-gray-500">
                            No profitability data for the selected filters.
                        </div>
                    )}

                    {!smoothCustomerLoading && customer && showCustomerContent && (
                        <div className="space-y-5">
                            <CustomerCard
                                customer={customer}
                                conversionType={conversionType}
                                onClick={() => setShowBranches(true)}
                            />
                            <ProfitabilityChart
                                customer={customer}
                                conversionType={conversionType}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Branch drill-down */}
            {showBranches && (
                <>
                    {smoothBranchLoading && (
                        <div className="space-y-6">
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} className="bg-white rounded-lg border shadow-sm">
                                    <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
                                    <div className="p-6 space-y-4">
                                        <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                                        <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!smoothBranchLoading && branches.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            No branch data found. Try adjusting your filters or search terms.
                        </div>
                    )}

                    {!smoothBranchLoading && branches.length > 0 && showBranchContent && (
                        <div className="space-y-6">
                            <div className="text-sm text-gray-600">
                                Showing {branches.length} of {branchTotal} branches
                                {debouncedBranchSearch && ` for "${debouncedBranchSearch}"`}
                            </div>

                            <div className="space-y-6">
                                {branches.map((branch) => (
                                    <BranchCard
                                        key={branch.ou_code}
                                        branch={branch}
                                        conversionType={conversionType}
                                    />
                                ))}
                            </div>

                            <PaginationFooter
                                page={branchPage}
                                pageSize={branchPageSize}
                                totalItems={branchTotal}
                                onPageChange={setBranchPage}
                                onPageSizeChange={(s) => {
                                    setBranchPage(1);
                                    setBranchPageSize(s);
                                }}
                                pageSizeOptions={[10, 20, 50]}
                                itemLabel="branches"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RevenueProfitabilityTab;
